import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  ButtonDropdownProps,
  ExpandableSection,
  FormField,
  InputProps,
  Modal,
  RadioGroup,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsPresetRequest,
  OmsPresetScope,
  OmsProduct,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import useOmsExport, { restoredOmsExportMode } from "../../hooks/useOmsExport";
import useOmsFilterCriteria, {
  OmsFilterDraft,
} from "../../hooks/useOmsFilterCriteria";
import useOmsModalHeight from "../../hooks/useOmsModalHeight";
import useOmsPresets, {
  presetErrorMessage,
  presetValidationSourceUnavailable,
  useOmsExportCapabilities,
} from "../../hooks/useOmsPresets";
import OMSColumns, {
  columnSelectionError,
  OmsColumnsDraft,
} from "./OMSColumns";
import OMSExportPanel, { OMSExportActions } from "./OMSExportPanel";
import OMSExportSource from "./OMSExportSource";
import OMSPresetToolbar from "./OMSPresetToolbar";
import { OMSPresetManager, OMSPresetSave } from "./OMSPresetEditor";
import OMSRecordFilters, { AppliedOmsFilters } from "./OMSRecordFilters";
import {
  applyPreset,
  OmsConfiguration,
  presetIsEdited,
} from "./omsPresetEditing";

export default function OMSExportDialog({
  product,
  productLabel,
  report,
  availableReport,
  onClose,
  draft,
  onSaveDraft,
}: {
  product: OmsProduct;
  productLabel: string;
  report: OmsReport;
  availableReport: OmsReport;
  onClose: () => void;
  draft?: OmsFilterDraft;
  onSaveDraft: (draft: OmsFilterDraft) => void;
}) {
  const [mode, setMode] = useState<"full" | "filtered">(() => {
    if (draft) return draft.tab === "filtered" ? "filtered" : "full";
    return restoredOmsExportMode(product, report);
  });
  const [focusFilters, setFocusFilters] = useState(false);
  const [focusColumns, setFocusColumns] = useState(false);
  const [columnsExpanded, setColumnsExpanded] = useState(
    draft?.columnsExpanded || false
  );
  const [columns, setColumns] = useState<OmsColumnsDraft>(
    draft?.columns || { strategy: "all", names: [] }
  );
  const [applied, setApplied] = useState(draft?.appliedPreset);
  const [undo, setUndo] = useState<OmsConfiguration>();
  const [announcement, setAnnouncement] = useState("");
  const [editor, setEditor] = useState<
    NonNullable<OmsFilterDraft["presetEditor"]>
  >(draft?.presetEditor || { view: "export", name: "", scope: "columns" });
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [saveValidation, setSaveValidation] = useState("");
  const saveRef = useRef<ButtonProps.Ref>(null);
  const actionsRef = useRef<ButtonDropdownProps.Ref>(null);
  const selectRef = useRef<SelectProps.Ref>(null);
  const nameRef = useRef<InputProps.Ref>(null);
  const editGeneration = useRef(0);
  const capabilities = useOmsExportCapabilities();
  const presetsEnabled = Boolean(capabilities.data?.presets);
  const columnsEnabled = Boolean(capabilities.data?.columnSelection);
  const presets = useOmsPresets(product, presetsEnabled, draft?.presetRequest);
  const full = useOmsExport(
    product,
    draft?.report || report,
    "full",
    availableReport,
    !draft?.editing,
    draft?.request?.mode === "full" ? draft.request : undefined
  );
  const filtered = useOmsExport(
    product,
    draft?.report || report,
    "filtered",
    availableReport,
    !draft?.editing,
    draft?.request?.mode === "filtered" ? draft.request : undefined
  );
  const current = mode === "full" ? full : filtered;
  const source = current.sourceReport;
  const criteria = useOmsFilterCriteria(source, filtered.job.data, draft);
  const active = Boolean(current.jobId) || current.create.isLoading;
  const accepted = current.job.data;
  const restored = useRef<string>();
  useEffect(() => {
    if (!accepted || restored.current === accepted.jobId) return;
    restored.current = accepted.jobId;
    setColumns({
      strategy: accepted.outputColumnStrategy || "all",
      names: accepted.outputColumns || [...source.columns],
    });
  }, [accepted, source.columns]);
  const configuration: OmsConfiguration = {
    mode,
    criteria: criteria.criteria,
    operation: criteria.operation,
    columns,
    appliedPreset: applied,
  };
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;
  const edited = Boolean(applied && presetIsEdited(applied, configuration));
  const columnError = columnSelectionError(columns, source);
  const validFilters = mode === "filtered" && criteria.valid;
  const previousRequest = current.matchesPreviousRequest({
    filters: mode === "full" ? [] : criteria.filters,
    operation: mode === "full" ? "and" : criteria.operation,
    outputColumns: columns.strategy === "selected" ? columns.names : undefined,
  });
  const validColumns =
    !columnError &&
    (columnsEnabled || columns.strategy === "all" || previousRequest);
  const preparing =
    current.create.isLoading ||
    (Boolean(current.jobId) &&
      !current.expired &&
      !current.unavailable &&
      !current.job.isError &&
      (!accepted || accepted.status === "PREPARING"));
  const { height, contentRef, titleRef, footerRef } = useOmsModalHeight();

  function changed() {
    editGeneration.current += 1;
    setUndo(undefined);
    setAnnouncement("");
  }
  function restore(next: OmsConfiguration) {
    full.editSettings(source);
    filtered.editSettings(source);
    setMode(next.mode);
    criteria.replace(next.criteria, next.operation);
    setColumns(next.columns);
    setApplied(next.appliedPreset);
  }
  const selectPreset = (id: string) => {
    const generation = editGeneration.current;
    presets.read
      .mutateAsync(id)
      .then((preset) => {
        if (generation !== editGeneration.current) {
          setAnnouncement(
            "Settings changed while the preset loaded. Choose it again to apply."
          );
          return;
        }
        const previous = configurationRef.current;
        const next = applyPreset(preset, previous, source);
        restore(next);
        setUndo(previous);
        setAnnouncement(`Applied ${preset.name}.`);
        if (columnSelectionError(next.columns, source)) {
          setColumnsExpanded(true);
          setFocusColumns(true);
        } else if (preset.scope !== "columns") setFocusFilters(true);
      })
      .catch(() => undefined);
  };
  const openSave = (
    updating = false,
    returnTo: "save" | "actions" = "save"
  ) => {
    presets.change.reset();
    setSaveValidation("");
    setSaveAttempted(false);
    let scope: OmsPresetScope = "columns";
    if (updating && applied) scope = applied.scope;
    else if (validFilters && validColumns) scope = "both";
    setEditor({
      view: "save",
      name: updating && applied ? applied.name : "",
      scope,
      updating,
      returnTo,
    });
  };
  const backToExport = () => {
    setEditor({ ...editor, view: "export" });
    window.setTimeout(() => {
      if (editor.returnTo === "actions") actionsRef.current?.focus();
      else saveRef.current?.focus();
    }, 0);
  };
  const savePreset = () => {
    setSaveAttempted(true);
    const name = editor.name.trim().normalize("NFC");
    if (!name || Array.from(name).length > 80 || /\p{C}/u.test(name)) {
      setSaveValidation(
        "Enter a name of 1–80 characters without control characters."
      );
      nameRef.current?.focus();
      return;
    }
    if (
      (editor.scope !== "columns" && !validFilters) ||
      (editor.scope !== "filters" && !validColumns)
    ) {
      setSaveValidation("Correct the settings included in this preset.");
      return;
    }
    const body: OmsPresetRequest = {
      name,
      scope: editor.scope,
      validationSnapshotId: source.snapshotId,
      ...(editor.scope !== "columns"
        ? { filters: criteria.filters, operation: criteria.operation }
        : {}),
      ...(editor.scope !== "filters"
        ? {
            columns:
              columns.strategy === "all"
                ? { strategy: "all" }
                : { strategy: "selected", names: columns.names },
          }
        : {}),
    };
    if (new Blob([JSON.stringify(body)]).size > 16 * 1024) {
      setSaveValidation(
        "These settings exceed the 16 KiB preset limit. You can still export them without saving."
      );
      return;
    }
    setSaveValidation("");
    presets.change
      .mutateAsync({
        action: "save",
        body,
        existing: editor.updating ? applied : undefined,
      })
      .then((result) => {
        if (result.preset) setApplied(result.preset);
        setAnnouncement(`Saved ${name}.`);
        setUndo(undefined);
        backToExport();
      })
      .catch(() => nameRef.current?.focus());
  };
  const close = () => {
    const retryRequest = current.requestIdentity();
    onSaveDraft({
      snapshotId: source.snapshotId,
      report: source,
      editing: !active,
      criteria: criteria.criteria,
      operation: criteria.operation,
      tab: mode,
      columns,
      columnsExpanded,
      appliedPreset: applied,
      presetEditor: editor,
      request: retryRequest ? { mode, ...retryRequest } : undefined,
      presetRequest: presets.changeIdentity(),
    });
    onClose();
  };
  const editableCriteria = {
    ...criteria,
    update: (...args: Parameters<typeof criteria.update>) => {
      changed();
      criteria.update(...args);
    },
    setOperator: (...args: Parameters<typeof criteria.setOperator>) => {
      changed();
      criteria.setOperator(...args);
    },
    setColumn: (...args: Parameters<typeof criteria.setColumn>) => {
      changed();
      criteria.setColumn(...args);
    },
    setOperation: (...args: Parameters<typeof criteria.setOperation>) => {
      changed();
      criteria.setOperation(...args);
    },
    add: () => {
      changed();
      criteria.add();
    },
    remove: (index: number) => {
      changed();
      criteria.remove(index);
    },
    clear: () => {
      changed();
      criteria.clear();
    },
  };
  const saveStatus = (
    presets.change.error as { response?: { status?: number } }
  )?.response?.status;
  let summaryColumns =
    columns.strategy === "all" ? source.columns : columns.names;
  let summaryStrategy = columns.strategy;
  if (active && accepted) {
    summaryColumns = accepted.outputColumns || source.columns;
    summaryStrategy = accepted.outputColumnStrategy || "all";
  }
  const columnSummary =
    summaryStrategy === "all"
      ? `All columns (${summaryColumns.length})`
      : `${summaryColumns.length} selected columns`;
  let title = `Export ${productLabel} report`;
  if (editor.view === "save")
    title = `${editor.updating ? "Update" : "Save"} preset · ${productLabel}`;
  else if (editor.view === "manage") title = `Manage presets · ${productLabel}`;

  return (
    <Modal
      visible
      onDismiss={close}
      header={<span ref={titleRef}>{title}</span>}
      size="x-large"
      position="top"
      height={height}
      closeAriaLabel="Close report export"
      footer={
        <div ref={footerRef} className="oms-report-download__footer">
          {editor.view === "export" ? (
            <>
              <Button
                variant={current.jobId && preparing ? "primary" : "normal"}
                onClick={close}
              >
                Close
              </Button>
              <OMSExportActions
                state={current}
                filters={mode === "full" ? [] : criteria.filters}
                operation={mode === "full" ? "and" : criteria.operation}
                outputColumns={
                  columns.strategy === "selected" ? columns.names : undefined
                }
                valid={
                  (mode === "full" || criteria.valid) &&
                  validColumns &&
                  !presets.read.isLoading
                }
                onConfigure={() => {
                  changed();
                  if (mode === "filtered") setFocusFilters(true);
                }}
              />
            </>
          ) : (
            <>
              <Button
                disabled={presets.change.isLoading}
                onClick={backToExport}
              >
                {editor.view === "save" ? "Cancel" : "Back to export"}
              </Button>
              {editor.view === "save" && (
                <Button
                  variant="primary"
                  loading={presets.change.isLoading}
                  loadingText="Saving preset"
                  onClick={savePreset}
                  disabled={
                    (editor.scope !== "columns" && !validFilters) ||
                    (editor.scope !== "filters" && !validColumns)
                  }
                >
                  {editor.updating ? "Update preset" : "Save preset"}
                </Button>
              )}
            </>
          )}
        </div>
      }
    >
      <div ref={contentRef}>
        {editor.view === "save" && (
          <SpaceBetween size="m">
            {(saveValidation || presets.change.isError) && (
              <Alert
                type="error"
                header="Could not save preset"
                action={
                  saveStatus === 412 ||
                  (saveStatus === 404 &&
                    !presetValidationSourceUnavailable(
                      presets.change.error
                    )) ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      {applied && (
                        <Button
                          onClick={() => {
                            selectPreset(applied.presetId);
                            backToExport();
                          }}
                        >
                          Reload preset
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          presets.change.reset();
                          setEditor({
                            ...editor,
                            updating: false,
                            name: `${editor.name.slice(0, 75)} copy`,
                          });
                        }}
                      >
                        Save as new
                      </Button>
                    </SpaceBetween>
                  ) : undefined
                }
              >
                {saveValidation || presetErrorMessage(presets.change.error)}
              </Alert>
            )}
            <OMSPresetSave
              name={editor.name}
              scope={editor.scope}
              onName={(name) => setEditor({ ...editor, name })}
              onScope={(scope: OmsPresetScope) =>
                setEditor({ ...editor, scope })
              }
              filterCount={criteria.filters.length}
              columnSummary={columnSummary}
              validFilters={validFilters}
              validColumns={validColumns}
              attempted={saveAttempted}
              nameRef={nameRef}
              disabled={presets.change.isLoading}
            />
          </SpaceBetween>
        )}
        {editor.view === "manage" && (
          <OMSPresetManager
            state={presets}
            onDeleted={(id) => {
              if (applied?.presetId === id) setApplied(undefined);
              setUndo(undefined);
              setAnnouncement(
                "Preset deleted. Applied settings are preserved."
              );
            }}
            onRenamed={(metadata) => {
              if (applied?.presetId === metadata.presetId)
                setApplied({ ...applied, ...metadata });
            }}
          />
        )}
        {editor.view === "export" && (
          <SpaceBetween size="l">
            <OMSExportSource
              report={source}
              availableReport={availableReport}
              accepted={active}
              onUseLatest={() => {
                changed();
                criteria.replace(
                  criteria.criteria.map((criterion) => {
                    const previousType =
                      source.columnTypes?.[criterion.column] || "string";
                    const nextType =
                      availableReport.columnTypes?.[criterion.column] ||
                      "string";
                    return availableReport.columns.includes(criterion.column) &&
                      previousType !== nextType
                      ? {
                          ...criterion,
                          valueNotice: `This field changed from ${previousType} to ${nextType}. Review its condition and value.`,
                        }
                      : criterion;
                  }),
                  criteria.operation
                );
                full.editSettings(availableReport);
                filtered.editSettings(availableReport);
                if (mode === "filtered") setFocusFilters(true);
                if (columns.strategy === "selected") {
                  setColumns({ ...columns, touched: true });
                  if (columnSelectionError(columns, availableReport)) {
                    setColumnsExpanded(true);
                    setFocusColumns(true);
                  }
                }
              }}
            />
            {(current.jobId || !current.create.isIdle) && (
              <OMSExportPanel state={current} mode={mode} />
            )}
            {presetsEnabled && (
              <OMSPresetToolbar
                state={presets}
                applied={applied}
                edited={edited}
                disabled={active}
                saveDisabled={active && accepted?.status !== "READY"}
                onApply={selectPreset}
                onSave={() => openSave()}
                onSaveAsNew={() => openSave(false, "actions")}
                onUpdate={() => openSave(true, "actions")}
                onManage={() => {
                  presets.change.reset();
                  setEditor({
                    ...editor,
                    view: "manage",
                    returnTo: "actions",
                  });
                }}
                saveRef={saveRef}
                selectRef={selectRef}
                actionsRef={actionsRef}
              />
            )}
            {announcement && (
              <div role="status" aria-live="polite">
                <Box color="text-body-secondary">
                  {announcement}{" "}
                  {undo && (
                    <Button
                      variant="inline-link"
                      onClick={() => {
                        restore(undo);
                        setUndo(undefined);
                        setAnnouncement("Previous settings restored.");
                        selectRef.current?.focus();
                      }}
                    >
                      Undo
                    </Button>
                  )}
                </Box>
              </div>
            )}
            {!active && (
              <>
                {source.rowCount === 0 && (
                  <Alert type="info" header="Empty report">
                    The export will contain headers only.
                  </Alert>
                )}
                <FormField
                  label="Rows"
                  constraintText="Dashboard filters do not apply."
                >
                  <RadioGroup
                    value={mode}
                    items={[
                      { value: "full", label: "All rows" },
                      { value: "filtered", label: "Filtered rows" },
                    ]}
                    onChange={({ detail }) => {
                      changed();
                      const nextMode = detail.value as "full" | "filtered";
                      const target = nextMode === "full" ? full : filtered;
                      if (
                        target.jobId ||
                        target.sourceReport.snapshotId !== source.snapshotId
                      )
                        target.editSettings(source);
                      setMode(nextMode);
                    }}
                  />
                </FormField>
                {mode === "filtered" && (
                  <OMSRecordFilters
                    report={source}
                    state={editableCriteria}
                    focusRequested={focusFilters}
                    onFocusHandled={() => setFocusFilters(false)}
                  />
                )}
                {columnsEnabled && (
                  <OMSColumns
                    report={source}
                    settings={columns}
                    expanded={columnsExpanded}
                    onExpand={setColumnsExpanded}
                    onChange={(settings) => {
                      changed();
                      setColumns(settings);
                    }}
                    focusRequested={focusColumns}
                    onFocusHandled={() => setFocusColumns(false)}
                  />
                )}
                {!columnsEnabled && columns.strategy === "selected" && (
                  <Alert type="warning">
                    New column selections are temporarily unavailable. Your
                    selection is preserved.
                    {previousRequest && " You can retry your previous request."}
                    <Button
                      variant="inline-link"
                      onClick={() => {
                        changed();
                        setColumns({ ...columns, strategy: "all" });
                      }}
                    >
                      Use all columns
                    </Button>
                  </Alert>
                )}
                <Box color="text-body-secondary">
                  One CSV with column headers, compressed in a ZIP.
                </Box>
              </>
            )}
            {active && accepted && (
              <SpaceBetween size="m">
                {(accepted?.csvFiles || 1) > 1 && (
                  <Box color="text-body-secondary">
                    This existing ZIP contains {accepted?.csvFiles} CSV files.
                    Edit settings to prepare one CSV.
                  </Box>
                )}
                <Box>
                  Rows:{" "}
                  {accepted.mode === "filtered" ? "Filtered rows" : "All rows"}
                </Box>
                {accepted.mode === "filtered" && (
                  <AppliedOmsFilters
                    report={source}
                    filters={accepted?.filters || criteria.filters}
                    operation={accepted?.operation || criteria.operation}
                    textCaseSensitive={accepted?.textCaseSensitive ?? true}
                    compact
                  />
                )}
                <ExpandableSection
                  variant="container"
                  headingTagOverride="h3"
                  headerText="Output columns"
                  headerDescription={columnSummary}
                >
                  <ol className="oms-report-download__applied-filters">
                    {(
                      accepted?.outputColumns ||
                      (columns.strategy === "all"
                        ? source.columns
                        : columns.names)
                    ).map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ol>
                </ExpandableSection>
              </SpaceBetween>
            )}
          </SpaceBetween>
        )}
      </div>
    </Modal>
  );
}
